// Left filter rail + Rule Builder drawer (shared criteria for alerts & saved searches)

import React from "react";
import { Badge, Chip, ComingSoon, Toggle } from "./atoms.jsx";
import {
  CHANNELS,
  CONFIDENCE_LEVELS,
  ITEM_TYPES,
  KARATS,
  SEED_ITEMS,
} from "./data.jsx";
import { I } from "./icons.jsx";
import "./filters.css";

const FilterGroup = ({ title, count, children, defaultOpen = true }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="filt-group">
      <div className="ghead" onClick={() => setOpen(!open)}>
        <h3>{title}</h3>
        <span className="count">{count != null ? `${count}` : ""} {open ? "−" : "+"}</span>
      </div>
      {open ? <div className="gbody">{children}</div> : null}
    </div>
  );
};

const ChkPill = ({ children, on, onClick, tone }) => (
  <button className={`chkpill ${on ? "on" : ""} ${tone || ""}`} onClick={onClick}>{children}</button>
);

const Slider = ({ fillStart = 0, fillEnd = 0.8, single }) => (
  <div className="slider-wrap">
    <div className="slider-track">
      <div className="slider-fill" style={{left: `${fillStart*100}%`, width: `${(fillEnd - fillStart)*100}%`}}/>
      {!single && <div className="slider-thumb" style={{left: `${fillStart*100}%`}}/>}
      <div className="slider-thumb" style={{left: `${fillEnd*100}%`}}/>
    </div>
  </div>
);

// ===== Public live stream control =====
const formatMatchCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString() : "—";
};

const LiveCard = ({ on, setOn, lastAnalyzedLabel, status = "paused", matchCount = null }) => {
  const label = on ? status : "paused";
  return (
    <div className={`live-card ${on ? "on" : ""}`} data-testid="public-live-card" data-status={label}>
      <div className="lc-top">
        <div className="lc-title"><span className="dot"/>Live - {label}</div>
        <Toggle on={on} onChange={setOn} variant="live" ariaLabel={on ? "Pause live stream" : "Start live stream"}/>
      </div>
      <div style={{fontSize:12, color: on ? "var(--ink-2)" : "var(--muted)", lineHeight:1.4}}>
        {on ? "Newly analyzed public items appear when they match this feed." : "Resume live updates without changing the cached feed."}
      </div>
      <div className="lc-meta">
        <div><div className="m-num" data-testid="public-live-match-count">{formatMatchCount(matchCount)}</div><div className="m-lbl">matches</div></div>
        <div><div className="m-num">{label}</div><div className="m-lbl">state</div></div>
        <div><div className="m-num" data-testid="public-live-last-analyzed">{lastAnalyzedLabel || "none"}</div><div className="m-lbl">last analyzed</div></div>
      </div>
    </div>
  );
};

const FilterRail = ({
  liveOn,
  setLiveOn,
  onOpenRuleBuilder,
  lastAnalyzedLabel,
  liveStatus,
  liveMatchCount,
  filters,
  setFilters,
  signedIn,
  onComingSoon,
  searchTerm,
  onSearch,
  onClearSearch,
  onApplyFilters,
  onResetFilters,
}) => {
  const [keyword, setKeyword] = React.useState(searchTerm || "");
  React.useEffect(() => { setKeyword(searchTerm || ""); }, [searchTerm]);
  const includeReviewRequired = filters.includeReviewRequired !== false;
  const toggleArr = (k, v) => {
    setFilters(prev => {
      const cur = prev[k] || [];
      return {...prev, [k]: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v]};
    });
  };
  const setIncludeReviewRequired = (next) => {
    setFilters(prev => ({...prev, includeReviewRequired: next}));
  };
  const submitKeyword = (event) => {
    event.preventDefault();
    onSearch?.(keyword);
  };
  const clearKeyword = () => {
    setKeyword("");
    onClearSearch?.();
  };
  return (
    <aside className="rail">
      <LiveCard on={liveOn} setOn={setLiveOn} lastAnalyzedLabel={lastAnalyzedLabel} status={liveStatus} matchCount={liveMatchCount}/>
      <div className="rail-head">
        <h2><span className="dot"/> Filters</h2>
        <div style={{display:"flex", gap:"6px"}}>
          <button className="btn xs ghost" title="Reset" onClick={onResetFilters}><I.refresh size={11}/></button>
          <button className="btn xs ghost" title="Save as search" onClick={onOpenRuleBuilder}><I.save size={11}/></button>
        </div>
      </div>
      <div className="rail-body">
        <FilterGroup title="Keywords">
          <form style={{position:"relative"}} onSubmit={submitKeyword}>
            <I.search size={13} style={{position:"absolute", left:"9px", top:"50%", transform:"translateY(-50%)", color:"var(--muted)"}}/>
            <input
              className="input"
              placeholder="Title contains..."
              style={{paddingLeft:"28px", paddingRight: keyword ? "32px" : undefined}}
              value={keyword}
              onChange={event => setKeyword(event.target.value)}
              aria-label="Filter keyword"
            />
            {keyword ? (
              <button className="btn icon sm ghost search-clear" type="button" onClick={clearKeyword} title="Clear keyword" style={{position:"absolute", right:"5px", top:"50%", transform:"translateY(-50%)"}}>
                <I.x size={12}/>
              </button>
            ) : null}
          </form>
          <label
            className="row-flex"
            style={{cursor:"pointer"}}
            onClick={() => setIncludeReviewRequired(!includeReviewRequired)}
          >
            <div>
              <div className="rlabel">Include review-required</div>
              <div className="rsub">Show items flagged for human review</div>
            </div>
            <Toggle on={includeReviewRequired} onChange={setIncludeReviewRequired} variant="accent" ariaLabel="Include review-required"/>
          </label>
        </FilterGroup>

        <FilterGroup title="Gold purity (karat)">
          <div className="checks">
            {KARATS.map(k => (
              <ChkPill key={k} tone="gold" on={(filters.karat||[]).includes(k)} onClick={() => toggleArr("karat", k)}>{k}K</ChkPill>
            ))}
          </div>
        </FilterGroup>

        <FilterGroup title="Item type">
          <div className="checks">
            {ITEM_TYPES.map(t => (
              <ChkPill key={t} on={(filters.type||[]).includes(t)} onClick={() => toggleArr("type", t)}>{t}</ChkPill>
            ))}
          </div>
        </FilterGroup>

        <ComingSoon
          variant="inline"
          feature="Advanced filters"
          detail="Price, weight, confidence, and saved rule criteria are staged for the account-feature rollout. The public MVP keeps keyword, karat, and item-type filters live."
          onAction={() => onComingSoon?.("Advanced filters", "Price, weight, confidence, and saved rule criteria are staged for the account-feature rollout. The public MVP keeps keyword, karat, and item-type filters live.")}
          actionLabel="Coming soon"
        />

        <button className="btn" style={{width:"100%", justifyContent:"center", marginTop:"10px"}} onClick={onOpenRuleBuilder}>
          <I.sliders size={14}/> Open rule builder
        </button>
      </div>
      <div className="rail-foot">
        <button className="btn primary" style={{flex:1, justifyContent:"center"}} onClick={onApplyFilters}><I.check size={13}/> Apply</button>
        <button className="btn" onClick={onResetFilters}>Reset</button>
      </div>
    </aside>
  );
};

// Active filter summary strip
const FilterStrip = ({
  onOpenRuleBuilder,
  signedIn,
  filters = {},
  setFilters,
  searchTerm,
  onClearSearch,
  onClearFilters,
}) => {
  const karats = filters.karat || [];
  const types = filters.type || [];
  const excludesReviewRequired = filters.includeReviewRequired === false;
  const hasActive = !!searchTerm || karats.length > 0 || types.length > 0 || excludesReviewRequired;
  return (
  <div className="chip-strip" data-testid="filter-strip">
    <span className="micro" style={{alignSelf:"center", marginRight:"4px"}}>Active</span>
    {searchTerm ? <Chip onRemove={onClearSearch}>&quot;{searchTerm}&quot;</Chip> : null}
    {karats.length ? (
      <Chip onRemove={() => setFilters?.(prev => ({...prev, karat: []}))} tone="gold">
        {karats.map(k => `${k}K`).join(", ")}
      </Chip>
    ) : null}
    {types.length ? (
      <Chip onRemove={() => setFilters?.(prev => ({...prev, type: []}))}>
        {types.join(", ")}
      </Chip>
    ) : null}
    {excludesReviewRequired ? (
      <Chip onRemove={() => setFilters?.(prev => ({...prev, includeReviewRequired: true}))} tone="hot">
        Excluding review-required
      </Chip>
    ) : null}
    {!hasActive ? <span className="mono" style={{fontSize:11, color:"var(--muted)"}}>No active public filters</span> : null}
    <button className="btn xs ghost" style={{marginLeft:"auto"}} onClick={onOpenRuleBuilder}>
      <I.sliders size={11}/> Rule builder
    </button>
    {hasActive ? <button className="btn xs ghost" onClick={onClearFilters}>Clear all</button> : null}
    <button className="btn xs" onClick={onOpenRuleBuilder}><I.save size={11}/> Save</button>
    </div>
  );
};

// ===== Rule Builder (criteria shared by alerts + saved searches) =====
const RBSlider = ({ label, value, fill }) => (
  <div className="rb-field">
    <div className="lbl-row"><label className="lbl">{label}</label><span className="val">{value}</span></div>
    <Slider single fillEnd={fill}/>
  </div>
);

const RuleBuilder = ({ onClose, mode = "rule", signedIn }) => {
  const [tab, setTab] = React.useState("criteria"); // criteria | test
  const isRule = mode === "rule";
  const testItems = React.useMemo(() => SEED_ITEMS.slice(0, 6).map(it => ({
    ...it, matched: it.ratio != null && it.ratio <= 0.90 && (it.karat >= 14),
  })), []);
  const matchedCount = testItems.filter(t => t.matched).length;

  return (
    <>
      <div className="scrim" onClick={onClose}/>
      <div className="rb-drawer" role="dialog">
        <div className="rb-head">
          <div>
            <h2>{isRule ? "Alert rule" : "Advanced filter"}</h2>
            <div className="sub">{isRule
              ? "Get pinged the moment a new listing matches. Same criteria power saved searches."
              : "Precision criteria across every analyzed attribute. Save it as a one-tap search."}</div>
          </div>
          <div style={{display:"flex", gap:"8px"}}>
            {isRule && <div className="seg">
              <button className={tab==="criteria"?"active":""} onClick={()=>setTab("criteria")}>Criteria</button>
              <button className={tab==="test"?"active":""} onClick={()=>setTab("test")}>Test</button>
            </div>}
            <button className="btn icon sm ghost" onClick={onClose}><I.x size={14}/></button>
          </div>
        </div>

        {isRule && (
          <div className="rb-name-row">
            <div style={{flex:1}}>
              <input className="input" placeholder="Rule name — e.g. Underpriced 18K (≥10g)" defaultValue="Underpriced 18K (≥10g)"/>
            </div>
            <div className="row-flex" style={{gap:8, padding:0}}>
              <span style={{fontSize:12, color:"var(--muted)"}}>Enabled</span>
              <Toggle on={true} onChange={()=>{}} variant="accent" ariaLabel="Enable alert rule"/>
            </div>
          </div>
        )}

        {(!isRule || tab === "criteria") && (
          <div className="rb-body">
            <div className="rb-sect">
              <h3><span className="n">1</span> Price &amp; value</h3>
              <div className="rb-grid">
                <RBSlider label="Max price-to-melt ratio" value="≤ 0.80×" fill={0.5}/>
                <RBSlider label="Min value tier" value="A or better" fill={0.7}/>
                <div className="rb-field"><label className="lbl">Max all-in price</label><input className="input mono" defaultValue="$3,000"/></div>
                <div className="rb-field"><label className="lbl">Min gold weight</label><input className="input mono" defaultValue="10.0 g"/></div>
              </div>
            </div>

            <div className="rb-sect">
              <h3><span className="n">2</span> Item</h3>
              <div style={{display:"flex", flexDirection:"column", gap:12}}>
                <div className="rb-field">
                  <label className="lbl">Item types</label>
                  <div className="checks">{ITEM_TYPES.slice(0,10).map((t,i) => <ChkPill key={t} on={i===2||i===5}>{t}</ChkPill>)}</div>
                </div>
                <div className="rb-field">
                  <label className="lbl">Karats</label>
                  <div className="checks">{KARATS.map((k,i) => <ChkPill key={k} tone="gold" on={k===18}>{k}K</ChkPill>)}</div>
                </div>
                <div className="rb-grid">
                  <div className="rb-field"><label className="lbl">Brand include <span style={{color:"var(--faint)", fontWeight:400}}>(filter only)</span></label><input className="input" placeholder="Tiffany, Cartier…"/></div>
                  <div className="rb-field"><label className="lbl">Brand exclude</label><input className="input" placeholder="costume, replica…"/></div>
                </div>
              </div>
            </div>

            <div className="rb-sect">
              <h3><span className="n">3</span> Material &amp; purity</h3>
              <div className="rb-field" style={{marginBottom:12}}>
                <label className="lbl">Primary metal</label>
                <div className="checks">{["gold","silver","platinum"].map(m => <ChkPill key={m} on={m==="gold"}>{m}</ChkPill>)}</div>
              </div>
              <div className="rb-toggle-row"><div><div className="rlabel">Require solid gold</div><div className="rsub">Exclude plated / filled / vermeil entirely</div></div><Toggle on={true} onChange={()=>{}} variant="accent" ariaLabel="Require solid gold"/></div>
              <div className="rb-toggle-row"><div><div className="rlabel">Allow gold-filled</div></div><Toggle on={false} onChange={()=>{}} ariaLabel="Allow gold-filled"/></div>
              <div className="rb-toggle-row"><div><div className="rlabel">Allow gold-plated</div></div><Toggle on={false} onChange={()=>{}} ariaLabel="Allow gold-plated"/></div>
              <div className="rb-toggle-row"><div><div className="rlabel">Allow vermeil</div></div><Toggle on={false} onChange={()=>{}} ariaLabel="Allow vermeil"/></div>
            </div>

            <div className="rb-sect">
              <h3><span className="n">4</span> Quality &amp; risk</h3>
              <div className="rb-grid" style={{marginBottom:6}}>
                <div className="rb-field"><label className="lbl">Min confidence</label><div className="checks">{CONFIDENCE_LEVELS.map((c,i)=><ChkPill key={c} on={i<2}>{c}</ChkPill>)}</div></div>
                <div className="rb-field"><label className="lbl">Max listing age</label><input className="input mono" defaultValue="60 min"/></div>
              </div>
              <div className="rb-toggle-row"><div><div className="rlabel">Allow review-required pricing</div><div className="rsub">Include items flagged for human review</div></div><Toggle on={false} onChange={()=>{}} ariaLabel="Allow review-required pricing"/></div>
              <div className="rb-toggle-row"><div><div className="rlabel">Require authenticity guarantee</div></div><Toggle on={false} onChange={()=>{}} ariaLabel="Require authenticity guarantee"/></div>
              <div className="rb-toggle-row"><div><div className="rlabel">Require jewelry</div><div className="rsub">Exclude scrap / coins / bullion</div></div><Toggle on={true} onChange={()=>{}} ariaLabel="Require jewelry"/></div>
              <div className="rb-toggle-row"><div><div className="rlabel">Best offer required</div></div><Toggle on={false} onChange={()=>{}} ariaLabel="Best offer required"/></div>
            </div>

            <div className="rb-sect">
              <h3><span className="n">5</span> Seller</h3>
              <div className="rb-field"><label className="lbl">Exclude sellers</label><input className="input mono" placeholder="seller_a, seller_b…"/></div>
            </div>

            {isRule && (
              <div className="rb-sect">
                <h3><span className="n">6</span> Notify via</h3>
                <div style={{display:"flex", flexDirection:"column", gap:8}}>
                  {CHANNELS.map(c => (
                    <label key={c.id} className="rb-toggle-row" style={{padding:"7px 0", cursor: c.status==="coming_soon" ? "default":"pointer"}}>
                      <div style={{display:"flex", alignItems:"center", gap:10}}>
                        <span className="a-ch"><span className="ico">{c.type==="discord_webhook"?<I.hash size={11}/>:c.type==="email"?<I.mail size={11}/>:<I.bell size={11}/>}</span></span>
                        <div><div className="rlabel">{c.label}</div><div className="rsub">{c.type.replace("_"," ")}{c.status==="coming_soon"?" · coming soon":""}</div></div>
                      </div>
                      {c.status==="coming_soon" ? <Badge tone="outline">soon</Badge> : <Toggle on={c.enabled} onChange={()=>{}} variant="accent" ariaLabel={`${c.label} channel`}/>}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {isRule && tab === "test" && (
          <div className="rb-body">
            <div className="rb-sect" style={{paddingTop:8}}>
              <div className="rb-test">
                <div className="rb-test-head">
                  <div className="t">Dry-run against recent listings — <b>{matchedCount}</b> of {testItems.length} would match</div>
                  <button className="btn xs"><I.refresh size={11}/> Re-run</button>
                </div>
                {testItems.map(t => (
                  <div key={t.id} className="rb-test-item">
                    <div style={{minWidth:0}}>
                      <div className="ti-t">{t.title}</div>
                      <div className="ti-m">{t.karat}K · {t.goldWeight ?? "?"}g · value tier {t.valueTier ? `${t.valueTier} tier` : "—"} · {t.matched ? "matched: ratio, karat, weight" : "missed: below thresholds"}</div>
                    </div>
                    <div className="ti-r" style={{color: t.ratio != null && t.ratio < 1 ? "var(--accent-deep)":"var(--ink-2)"}}>{t.ratio != null ? `${t.ratio.toFixed(2)}×` : "—"}</div>
                    <div className={`ti-x ${t.matched ? "yes":"no"}`}>{t.matched ? <I.check size={11}/> : <I.x size={11}/>}</div>
                  </div>
                ))}
              </div>
              <div style={{display:"flex", gap:8, marginTop:14, alignItems:"center"}}>
                <button className="btn"><I.refresh size={13}/> Rescan recent window</button>
                <span style={{fontSize:11, color:"var(--muted)"}}>Re-evaluates the rule against the last 24h and queues matches.</span>
              </div>
            </div>
          </div>
        )}

        <div className="rb-foot">
          <div className="mono" style={{fontSize:11, color:"var(--muted)"}}>{isRule ? "v4 · 11 criteria set" : "11 criteria active"}</div>
          <div style={{display:"flex", gap:"8px"}}>
            {isRule
              ? <>
                  <button className="btn"><I.bolt size={13}/> Test</button>
                  <button className="btn primary"><I.check size={13}/> Save rule</button>
                </>
              : <>
                  <button className="btn"><I.save size={13}/> Save as search</button>
                  <button className="btn primary"><I.check size={13}/> Apply filter</button>
                </>}
          </div>
        </div>
      </div>
    </>
  );
};

export { ChkPill, FilterGroup, FilterRail, FilterStrip, RuleBuilder, Slider };
